import React, { useState } from "react";
import Particles from "../../Background/Particles";
import { Fade } from "react-awesome-reveal";
import type { Certificate, ImageModal } from "../../types/resume";

interface CertificatesProps {
  data: Certificate[];
}

const Certificates = ({ data }: CertificatesProps) => {
  const [modal, setModal] = useState<ImageModal>({
    isOpen: false,
    image: "",
    title: "",
  });

  const openModal = (image: string, title: string) => {
    setModal({ isOpen: true, image, title });
  };

  const closeModal = () => {
    setModal({ isOpen: false, image: "", title: "" });
  };

  if (!Array.isArray(data)) {
    console.error("Certificates: data is not an array:", data);
    return null;
  }

  const certificates = data.map((cert, index) => {
    return (
      <div
        key={index}
        className="group relative overflow-hidden rounded-xl shadow-xl shadow-black/30 transition-all duration-300 hover:shadow-sky-500/20 hover:-translate-y-2 hover:scale-105"
      >
        <img
          src={cert.image}
          alt={cert.title}
          width={1200}
          height={675}
          className="h-96 w-full object-cover transition-all duration-300 group-hover:scale-110 group-hover:blur-[2px]"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/70 p-4 text-center opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
          <h4 className="transform text-2xl font-bold text-sky-400 opacity-0 transition-all duration-300 -translate-y-4 group-hover:delay-100 group-hover:translate-y-0 group-hover:opacity-100">
            {cert.title}
          </h4>
          <div className="my-4 flex flex-col justify-center gap-2 text-lg text-slate-300 opacity-0 transition-all duration-300 group-hover:delay-200 group-hover:opacity-100">
            <p className="font-semibold">{cert.issuer}</p>
            <p className="text-sm">{cert.date}</p>
          </div>
          <div className="transform-gpu opacity-0 transition-all duration-300 group-hover:delay-300 group-hover:translate-y-0 group-hover:opacity-100 mt-6 flex translate-y-4 gap-4">
            <a
              href={cert.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-6 py-3 text-base font-semibold !text-white shadow-sm transition-colors duration-300 hover:bg-orange-500"
            >
              <i className="fa-solid fa-arrow-up-right-from-square"></i>
              Verify
            </a>
            <button
              onClick={() => openModal(cert.image, cert.title)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-700 px-6 py-3 text-base font-semibold text-slate-200 shadow-sm transition-colors duration-300 hover:bg-slate-400"
            >
              <i className="fa-solid fa-eye"></i>
              View
            </button>
          </div>
        </div>
      </div>
    );
  });

  return (
    <section
      id="certificates"
      className="relative min-h-screen flex flex-col items-center justify-center py-24 text-white overflow-hidden"
    >
        {/* Background Particles */}
       <div className="absolute top-0 left-0 w-full h-full">
        <Particles
          particleColors={["#ffffff"]}
          particleCount={60}
          particleSpread={25}
          speed={0.05}
          particleBaseSize={32}
          alphaParticles={false}
          disableRotation={true}
          pixelRatio={1}
        />
      </div>

      {/* Title Section */}
      <Fade direction="up" duration={1200} triggerOnce>
        <div className="relative z-10 max-w-7xl w-full px-6">
          <h1 className="text-center text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-400 drop-shadow-lg mb-16">
            My Certifications
          </h1>

          {/* Certificates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 ">
            {certificates}
          </div>
        </div>
      </Fade>

      {/* Modal */}
      {modal.isOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-slate-900/80 z-50 backdrop-blur-md p-4"
          onClick={closeModal}
        >
          <div
            className="relative bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-6 max-w-6xl w-full transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-3 right-4 text-slate-400 hover:text-white text-3xl font-bold transition-colors"
              onClick={closeModal}
            >
              &times;
            </button>

            {/* Modal Image */}
            <img
              src={modal.image}
              alt={modal.title}
              width={1200}
              height={675}
              className="w-full max-h-[85vh] object-contain rounded-lg shadow-lg border border-slate-700"
            />

            {/* Modal Title */}
            <div className="text-center mt-4 text-2xl font-semibold text-sky-400 tracking-wide">
              {modal.title}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Certificates;
